import React, { useState, useMemo } from 'react';
import { Modal, Button, Form, Badge, Spinner, Nav } from 'react-bootstrap';
import { ChevronRight, ChevronDown, Search, X, Info } from 'lucide-react';
import { Building } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { translate } from '../../../types/EnumTranslations';
import { getBaseTypeOrder } from '../../../Helpers/baseTypeUtils';
import { BaseType } from '../../../types/Enums';
import ComponentDetailsView from '../../ComponentPage/ComponentDetailsView';
import { resolveComponentLocation } from '../../../Helpers/ComponentRoomUtils';
import { liveRoofKeys } from '../../../Helpers/roofPlan';
import { drawingViews, listGroupForComponent } from '../../../Helpers/componentDrawing';
import ComponentDrawingPicker from './ComponentDrawingPicker';
import { ComponentFieldPatch } from '../../../Helpers/componentPatch';
import './ComponentPickerModal.css';

/** Base types usually placed away from a surface: the picker shows their room and level. */
const isRoomBoundBaseType = (baseType: BaseType): boolean =>
    baseType === BaseType.Utility || baseType === BaseType.FixedFurnishing;

type PickerTab = 'list' | 'drawing';

interface ComponentItem {
    key: string;
    component: BaseComponentImpl;
    displayName: string;
    baseType: BaseType;
    levelKey?: string;
    levelName?: string;
    roomKey?: string;
    roomName?: string;
    /** displayName of the parent surface (e.g. the facade a window sits on). */
    parentName?: string;
}

interface ComponentPickerModalProps {
    show: boolean;
    onHide: () => void;
    building: Building;
    currentComponentKey?: string;
    /** Single-select callback. Required unless multiSelect is set. */
    onSelect?: (componentKey: string | null) => void;
    saving?: boolean;
    /**
     * Opt-in multi-select mode: renders checkboxes instead of radios and
     * confirms the whole selection via onSelectMultiple. When omitted the
     * picker stays single-select (the default used by the issue and
     * work-order edit flows).
     */
    multiSelect?: boolean;
    /** Initial selection for multi-select mode. */
    selectedKeys?: string[];
    /** Called with the full set of chosen keys when saving in multi-select mode. */
    onSelectMultiple?: (componentKeys: string[]) => void;
    /** Optional predicate to limit the tree, e.g. to electrical utilities for the parking charger picker. */
    filter?: (component: BaseComponentImpl) => boolean;
    /** Optional muted note shown above the search field. */
    hint?: string;
    /**
     * Optional muted detail per item, shown instead of the default location
     * label (e.g. which parking spots a charger is linked to).
     */
    getItemDetail?: (component: BaseComponentImpl) => string | undefined;
    /** Optional rank within each room group; lower first, then by name. */
    getItemSortRank?: (component: BaseComponentImpl) => number;
    /**
     * For a caller that keeps its own copy of the building: an edit saved in
     * the component preview, to apply to that copy.
     */
    onComponentPatched?: (componentKey: string, patch: ComponentFieldPatch) => void;
}

/**
 * Modal for selecting component(s) from a building's component tree.
 * Single-select by default; pass `multiSelect` to choose several at once.
 * Two tabs: the searchable list, and the building's pictures (satellite
 * image, level drawings, takplan) with the components pinned on them.
 */
const ComponentPickerModal: React.FC<ComponentPickerModalProps> = ({
    show,
    onHide,
    building,
    currentComponentKey,
    onSelect,
    saving = false,
    multiSelect = false,
    selectedKeys,
    onSelectMultiple,
    filter,
    hint,
    getItemDetail,
    getItemSortRank,
    onComponentPatched
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedKey, setSelectedKey] = useState<string | null>(currentComponentKey || null);
    const [selectedKeySet, setSelectedKeySet] = useState<Set<string>>(new Set(selectedKeys || []));
    const [collapsedBaseTypes, setCollapsedBaseTypes] = useState<Set<string>>(new Set());
    const [collapsedLevels, setCollapsedLevels] = useState<Set<string>>(new Set());
    const [collapsedRooms, setCollapsedRooms] = useState<Set<string>>(new Set());
    const [previewComponentKey, setPreviewComponentKey] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<PickerTab>('list');

    // Stable dependency for the reset effect so a new array identity each render
    // doesn't wipe the user's in-modal selection.
    const initialKeysJoined = (selectedKeys || []).join(',');

    // Reset selection when modal opens with new initial selection
    React.useEffect(() => {
        if (show) {
            setSelectedKey(currentComponentKey || null);
            setSelectedKeySet(new Set(selectedKeys || []));
            setSearchQuery('');
            setPreviewComponentKey(null);
            setActiveTab('list');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, currentComponentKey, initialKeysJoined]);

    // Every live component, for the drawings (a component outside the
    // caller's filter is still drawn there, muted, so the picture stays whole).
    const liveComponents = useMemo((): BaseComponentImpl[] => {
        const components = building?.model?.structure?.components;
        if (!components) return [];
        return Object.entries(components)
            .filter(([, component]) => component.type && !component.archivedAt)
            .map(([key, component]) => new BaseComponentImpl(component.key ? component : { ...component, key }));
    }, [building]);

    // Build component items for the tree
    const componentItems = useMemo((): ComponentItem[] => {
        if (!building?.model?.structure?.components) return [];

        const structure = building.model.structure;
        const components = building.model.structure.components;
        const roofKeys = liveRoofKeys(structure);

        return liveComponents
            .map((component): ComponentItem | null => {
                // Caller-supplied filter (e.g. only electrical utilities)
                if (filter && !filter(component)) return null;

                // Resolve the room through the full parent hierarchy (own
                // roomKey → parentRoomKeys → parent surface). Utilities and
                // membranes store their room in parentRoomKeys rather than
                // roomKey, so the previous roomKey-only lookup dropped every
                // utility into the "Utan plan" bucket.
                const location = resolveComponentLocation(component, structure);
                // The level group follows the drawing tab: the level the
                // component is drawn on, else "Takplan" for the roof and what
                // sits on it, else "Fasad"/"Utomhus"; "Utan plan" when none.
                const group = listGroupForComponent(component, structure, roofKeys);
                const levelKey = group?.key;
                const levelName = group?.label;
                const roomKey = location.roomKey ?? undefined;
                const roomName = location.roomDisplay ?? undefined;

                // Resolve the parent surface (the component this one sits on) so
                // the picker can disambiguate otherwise identical labels — e.g. a
                // window shown as "Fönster 3" gains the facade it belongs to.
                const parentSurfaceKey =
                    component.parentSurface?.value ||
                    component.parentSurface1?.value ||
                    component.parentSurface2?.value;
                const parentData = parentSurfaceKey
                    ? components[parentSurfaceKey]
                    : undefined;
                const parentName = parentData
                    ? new BaseComponentImpl(parentData).displayName(building)
                    : undefined;

                return {
                    key: component.key,
                    component,
                    displayName: component.displayName(building),
                    baseType: component.type,
                    levelKey,
                    levelName,
                    roomKey,
                    roomName,
                    parentName
                };
            })
            .filter((item): item is ComponentItem => item !== null);
    }, [building, liveComponents, filter]);

    const itemsByKey = useMemo(() => new Map(componentItems.map(item => [item.key, item])), [componentItems]);
    const pickableKeys = useMemo((): ReadonlySet<string> => new Set(itemsByKey.keys()), [itemsByKey]);
    // The drawing tab needs at least one picture to show.
    const hasDrawings = useMemo(() => drawingViews(building?.model).length > 0, [building]);

    // Filter components based on search query
    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) return componentItems;

        const query = searchQuery.toLowerCase();
        return componentItems.filter(item =>
            item.displayName.toLowerCase().includes(query) ||
            (item.parentName?.toLowerCase().includes(query) ?? false)
        );
    }, [componentItems, searchQuery]);

    // Group components by BaseType -> Level -> Room
    const groupedComponents = useMemo(() => {
        const groups: Map<BaseType, Map<string, Map<string, ComponentItem[]>>> = new Map();

        filteredItems.forEach(item => {
            if (!groups.has(item.baseType)) {
                groups.set(item.baseType, new Map());
            }
            const baseTypeGroup = groups.get(item.baseType)!;

            const levelId = item.levelKey || '_no_level';
            if (!baseTypeGroup.has(levelId)) {
                baseTypeGroup.set(levelId, new Map());
            }
            const levelGroup = baseTypeGroup.get(levelId)!;

            const roomId = item.roomKey || '_no_room';
            if (!levelGroup.has(roomId)) {
                levelGroup.set(roomId, []);
            }
            levelGroup.get(roomId)!.push(item);
        });

        // Sort components within each room
        groups.forEach(baseTypeGroup => {
            baseTypeGroup.forEach(levelGroup => {
                levelGroup.forEach(components => {
                    components.sort((a, b) => {
                        if (getItemSortRank) {
                            const byRank = getItemSortRank(a.component) - getItemSortRank(b.component);
                            if (byRank !== 0) return byRank;
                        }
                        return a.displayName.localeCompare(b.displayName, 'sv');
                    });
                });
            });
        });

        if (!getItemSortRank) return groups;

        // With a rank, level and room groups are also ordered by their best
        // (lowest) ranked item, so e.g. the parking area's room comes first.
        const bestRank = (items: ComponentItem[]) =>
            items.reduce((best, item) => Math.min(best, getItemSortRank(item.component)), Number.POSITIVE_INFINITY);
        const ordered: typeof groups = new Map();
        groups.forEach((baseTypeGroup, baseType) => {
            const levels = Array.from(baseTypeGroup.entries()).map(([levelId, levelGroup]) => {
                const rooms = Array.from(levelGroup.entries())
                    .map(([roomId, items]) => ({ roomId, items, rank: bestRank(items) }))
                    .sort((a, b) => a.rank - b.rank);
                return {
                    levelId,
                    rooms: new Map(rooms.map(room => [room.roomId, room.items])),
                    rank: rooms.length > 0 ? rooms[0].rank : Number.POSITIVE_INFINITY,
                };
            }).sort((a, b) => a.rank - b.rank);
            ordered.set(baseType, new Map(levels.map(level => [level.levelId, level.rooms])));
        });
        return ordered;
    }, [filteredItems, getItemSortRank]);

    // Toggle base type collapsed state
    const toggleBaseTypeCollapsed = (baseType: BaseType) => {
        setCollapsedBaseTypes(prev => {
            const next = new Set(prev);
            if (next.has(baseType)) {
                next.delete(baseType);
            } else {
                next.add(baseType);
            }
            return next;
        });
    };

    // Toggle level collapsed state
    const toggleLevelCollapsed = (levelKey: string) => {
        setCollapsedLevels(prev => {
            const next = new Set(prev);
            if (next.has(levelKey)) {
                next.delete(levelKey);
            } else {
                next.add(levelKey);
            }
            return next;
        });
    };

    // Toggle room collapsed state
    const toggleRoomCollapsed = (roomKey: string) => {
        setCollapsedRooms(prev => {
            const next = new Set(prev);
            if (next.has(roomKey)) {
                next.delete(roomKey);
            } else {
                next.add(roomKey);
            }
            return next;
        });
    };

    // Count components in a group
    const countComponentsInBaseType = (baseType: BaseType): number => {
        const group = groupedComponents.get(baseType);
        if (!group) return 0;
        let count = 0;
        group.forEach(levelGroup => {
            levelGroup.forEach(components => {
                count += components.length;
            });
        });
        return count;
    };

    const countComponentsInLevel = (baseType: BaseType, levelKey: string): number => {
        const baseTypeGroup = groupedComponents.get(baseType);
        if (!baseTypeGroup) return 0;
        const levelGroup = baseTypeGroup.get(levelKey);
        if (!levelGroup) return 0;
        let count = 0;
        levelGroup.forEach(components => {
            count += components.length;
        });
        return count;
    };

    const handleSave = () => {
        if (multiSelect) {
            onSelectMultiple?.(Array.from(selectedKeySet));
        } else {
            onSelect?.(selectedKey);
        }
    };

    const toggleKeyInSet = (key: string) => {
        setSelectedKeySet(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // Get the component for preview
    const previewComponent = useMemo(() => {
        if (!previewComponentKey || !building?.model?.structure?.components) return null;
        const componentData = building.model.structure.components[previewComponentKey];
        if (!componentData) return null;
        return new BaseComponentImpl(componentData);
    }, [previewComponentKey, building]);

    // Handle info button click
    const handleInfoClick = (e: React.MouseEvent, componentKey: string) => {
        e.stopPropagation();
        e.preventDefault();
        setPreviewComponentKey(componentKey);
    };

    // Render a component row with selection control and info icon
    const renderComponentRow = (comp: ComponentItem) => {
        // Build the muted location suffix shown after the name. Utilities and fast
        // inredning are placed away from a surface, so show their room and level
        // (when known); other components fall back to the parent surface they
        // sit on (e.g. the facade a window belongs to).
        const locationLabel = getItemDetail
            ? getItemDetail(comp.component)
            : (
                isRoomBoundBaseType(comp.baseType)
                    ? [comp.roomName, comp.levelName].filter(Boolean)
                    : comp.parentName ? [comp.parentName] : []
            ).join(' · ');

        return (
        <div key={comp.key} className="d-flex align-items-center py-1">
            <Form.Check
                type={multiSelect ? 'checkbox' : 'radio'}
                id={`comp-${comp.key}`}
                name="component-selection"
                label={
                    locationLabel
                        ? <span>{comp.displayName}{' '}<span className="text-muted small">· {locationLabel}</span></span>
                        : comp.displayName
                }
                checked={multiSelect ? selectedKeySet.has(comp.key) : selectedKey === comp.key}
                onChange={() => (multiSelect ? toggleKeyInSet(comp.key) : setSelectedKey(comp.key))}
                className="flex-grow-1 mb-0"
            />
            <Button
                variant="link"
                size="sm"
                className="p-0 ms-2 text-muted"
                onClick={(e) => handleInfoClick(e, comp.key)}
                title="Visa komponentdetaljer"
            >
                <Info size={16} />
            </Button>
        </div>
        );
    };

    const sortedBaseTypes = Array.from(groupedComponents.keys())
        .sort((a, b) => getBaseTypeOrder(a) - getBaseTypeOrder(b));

    const pickFromDrawing = (key: string) => (multiSelect ? toggleKeyInSet(key) : setSelectedKey(key));

    const selectedItemLabel = (key: string) => itemsByKey.get(key)?.displayName ?? key;
    const selectedItemLocation = (key: string) => {
        const item = itemsByKey.get(key);
        if (!item) return undefined;
        return (
            isRoomBoundBaseType(item.baseType)
                ? [item.roomName, item.levelName].filter(Boolean)
                : item.parentName ? [item.parentName] : []
        ).join(' · ') || undefined;
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            className="component-picker-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>{multiSelect ? 'Välj komponenter' : 'Välj komponent'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {hint && <p className="text-muted small mb-2">{hint}</p>}

                {hasDrawings && (
                    <Nav variant="tabs" className="mb-3">
                        <Nav.Item>
                            <Nav.Link active={activeTab === 'list'} onClick={() => setActiveTab('list')}>Lista</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link active={activeTab === 'drawing'} onClick={() => setActiveTab('drawing')}>Ritning/Karta</Nav.Link>
                        </Nav.Item>
                    </Nav>
                )}

                {activeTab === 'drawing' && hasDrawings ? (
                    <ComponentDrawingPicker
                        building={building}
                        components={liveComponents}
                        pickableKeys={pickableKeys}
                        multiSelect={multiSelect}
                        selectedKey={selectedKey}
                        selectedKeys={selectedKeySet}
                        onPick={pickFromDrawing}
                    />
                ) : (
                <>
                {/* Search Input */}
                <div className="mb-3 position-relative">
                    <Form.Control
                        type="text"
                        placeholder="Sök komponent..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="ps-5"
                    />
                    <Search
                        size={18}
                        className="position-absolute text-muted"
                        style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }}
                    />
                    {searchQuery && (
                        <Button
                            variant="link"
                            size="sm"
                            className="position-absolute p-0"
                            style={{ right: '12px', top: '50%', transform: 'translateY(-50%)' }}
                            onClick={() => setSearchQuery('')}
                        >
                            <X size={18} className="text-muted" />
                        </Button>
                    )}
                </div>

                {/* Component Tree */}
                <div className="component-tree border rounded p-3">
                    {/* Option to clear selection (single-select only) */}
                    {!multiSelect && currentComponentKey && (
                        <div className="mb-3 pb-2 border-bottom">
                            <Form.Check
                                type="radio"
                                id="comp-none"
                                name="component-selection"
                                label={<span className="text-muted">Ingen komponent</span>}
                                checked={selectedKey === null}
                                onChange={() => setSelectedKey(null)}
                            />
                        </div>
                    )}

                    {sortedBaseTypes.map(baseType => {
                        const isCollapsed = collapsedBaseTypes.has(baseType);
                        const count = countComponentsInBaseType(baseType);
                        const baseTypeGroup = groupedComponents.get(baseType)!;

                        return (
                            <div key={baseType} className="mb-2">
                                <div
                                    className="d-flex align-items-center cursor-pointer py-1"
                                    onClick={() => toggleBaseTypeCollapsed(baseType)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                                    <span className="fw-bold ms-1">{translate(baseType)}</span>
                                    <Badge bg="secondary" className="ms-2">{count}</Badge>
                                </div>

                                {!isCollapsed && (
                                    <div className="ms-4">
                                        {Array.from(baseTypeGroup.keys()).map(levelKey => {
                                            const levelGroup = baseTypeGroup.get(levelKey)!;
                                            const levelName = levelKey === '_no_level'
                                                ? 'Utan plan'
                                                : filteredItems.find(c => c.levelKey === levelKey)?.levelName || levelKey;
                                            const levelCount = countComponentsInLevel(baseType, levelKey);
                                            const levelCollapsed = collapsedLevels.has(`${baseType}_${levelKey}`);

                                            return (
                                                <div key={levelKey} className="mb-1">
                                                    <div
                                                        className="d-flex align-items-center cursor-pointer py-1"
                                                        onClick={() => toggleLevelCollapsed(`${baseType}_${levelKey}`)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {levelCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                                        <span className="ms-1">{levelName}</span>
                                                        <Badge bg="light" text="dark" className="ms-2">{levelCount}</Badge>
                                                    </div>

                                                    {!levelCollapsed && (
                                                        <div className="ms-4">
                                                            {Array.from(levelGroup.keys()).map(roomKey => {
                                                                const components = levelGroup.get(roomKey)!;
                                                                const roomName = roomKey === '_no_room'
                                                                    ? 'Utan rum'
                                                                    : components[0]?.roomName || roomKey;
                                                                const roomCollapsed = collapsedRooms.has(`${baseType}_${levelKey}_${roomKey}`);

                                                                // If there's only one component and no room, show it directly
                                                                if (roomKey === '_no_room' && components.length === 1) {
                                                                    return renderComponentRow(components[0]);
                                                                }

                                                                return (
                                                                    <div key={roomKey} className="mb-1">
                                                                        {roomKey !== '_no_room' && (
                                                                            <div
                                                                                className="d-flex align-items-center cursor-pointer py-1"
                                                                                onClick={() => toggleRoomCollapsed(`${baseType}_${levelKey}_${roomKey}`)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            >
                                                                                {roomCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                                                                                <span className="ms-1 small">{roomName}</span>
                                                                                <Badge bg="light" text="dark" className="ms-2" style={{ fontSize: '0.7em' }}>
                                                                                    {components.length}
                                                                                </Badge>
                                                                            </div>
                                                                        )}

                                                                        {(roomKey === '_no_room' || !roomCollapsed) && (
                                                                            <div className={roomKey !== '_no_room' ? 'ms-4' : ''}>
                                                                                {components.map(comp => renderComponentRow(comp))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {sortedBaseTypes.length === 0 && !searchQuery && (
                        <p className="text-muted mb-0">Inga komponenter hittades i denna byggnad.</p>
                    )}

                    {sortedBaseTypes.length === 0 && searchQuery && (
                        <p className="text-muted mb-0">Inga komponenter matchar sökningen.</p>
                    )}
                </div>
                </>
                )}

                {/* Selected component info, always shown: what is chosen, with a way to undo it from either tab */}
                {!multiSelect && (
                    <div className="mt-3 p-2 bg-light rounded d-flex align-items-center justify-content-between flex-shrink-0">
                        <span className="small">
                            <strong>Vald:</strong>{' '}
                            {selectedKey ? (
                                <>
                                    {selectedItemLabel(selectedKey)}
                                    {selectedItemLocation(selectedKey) && (
                                        <span className="text-muted"> · {selectedItemLocation(selectedKey)}</span>
                                    )}
                                </>
                            ) : (
                                <span className="text-muted">ingen komponent</span>
                            )}
                        </span>
                        {selectedKey && (
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 ms-2 text-muted"
                                onClick={() => setSelectedKey(null)}
                                title="Rensa val"
                                aria-label="Rensa val"
                            >
                                <X size={16} />
                            </Button>
                        )}
                    </div>
                )}
                {multiSelect && (
                    <div className="mt-3 p-2 bg-light rounded d-flex flex-wrap align-items-center gap-1 flex-shrink-0">
                        <span className="small me-1"><strong>Valda ({selectedKeySet.size}):</strong></span>
                        {selectedKeySet.size === 0 && <span className="small text-muted">inga komponenter</span>}
                        {Array.from(selectedKeySet).map(key => (
                            <Badge key={key} bg="secondary" className="d-inline-flex align-items-center gap-1 fw-normal">
                                {selectedItemLabel(key)}
                                <button
                                    type="button"
                                    className="btn btn-link p-0 text-white lh-1"
                                    onClick={() => toggleKeyInSet(key)}
                                    title="Ta bort"
                                    aria-label={`Ta bort ${selectedItemLabel(key)}`}
                                >
                                    <X size={12} />
                                </button>
                            </Badge>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Avbryt
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving || (!multiSelect && selectedKey === (currentComponentKey ?? null))}
                >
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sparar...
                        </>
                    ) : (
                        'Spara'
                    )}
                </Button>
            </Modal.Footer>

            {/* Component Preview Modal */}
            <Modal
                show={!!previewComponent}
                onHide={() => setPreviewComponentKey(null)}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {previewComponent?.displayName(building)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {previewComponent && (
                        <ComponentDetailsView
                            component={previewComponent}
                            building={building}
                            buildingKey={building.key}
                            showHeader={false}
                            containerClassName=""
                            containerStyle={{ padding: 0 }}
                            onComponentPatched={onComponentPatched}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setPreviewComponentKey(null)}>
                        Stäng
                    </Button>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
};

export default ComponentPickerModal;
